// Seeded daily video rotation for the feed.
// Deterministic per (user, local date): the same user sees the same order
// all day, a different order tomorrow, and other users see different orders.

// FNV-1a 32-bit string hash
export function hashStringToSeed(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// Mulberry32 PRNG — returns () => float in [0, 1)
export function mulberry32(seed) {
  let a = seed;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Local date (not UTC) so rotation flips at the user's midnight
export function dailySeed(userId) {
  const d = new Date();
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${userId || 'anon'}:${ymd}`;
}

const DAY_MS = 86_400_000;

// Weighted seeded shuffle. Fresh community submissions are pinned to the
// front (newest first) so a member who submits a video reliably sees it in
// the feed — a multiplicative boost only gave them ~50/50 odds against the
// videoCap, and the daily seed meant refreshing never changed it. Everything
// else rotates behind them, with curated videos floating up but not pinned.
export function rotateVideos(videos, seedStr, opts = {}) {
  const { curatedBoost = 1.5, recentDays = 7, maxPinned = 3 } = opts;
  const now = Date.now();

  // ⚠️ Measured from when the video became VISIBLE (visibleSince = reviewed_at),
  // not when it was submitted. Review is not instant for an upload — a
  // moderator has to watch it — so measuring from submission spent the window
  // while the member was still waiting, and a video approved after 7 days was
  // never pinned at all. submittedAt is the fallback for sources that have no
  // review step.
  const submittedAge = (video) => {
    if (!video.isSubmission) return Infinity;
    const since = video.visibleSince || video.submittedAt;
    return since ? now - new Date(since).getTime() : Infinity;
  };

  const pinned = videos
    .filter((v) => submittedAge(v) < recentDays * DAY_MS)
    .sort((a, b) => submittedAge(a) - submittedAge(b))
    .slice(0, maxPinned);

  const pinnedIds = new Set(pinned.map((v) => v.id));

  const sortKey = (video) => {
    const r = mulberry32(hashStringToSeed(`${seedStr}:${video.id}`))();
    return r * (video.isCurated ? curatedBoost : 1);
  };

  const rotated = videos
    .filter((v) => !pinnedIds.has(v.id))
    .map((video) => [sortKey(video), video])
    .sort((a, b) => b[0] - a[0])
    .map(([, video]) => video);

  return [...pinned, ...rotated];
}
